import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  ShoppingCart,
  Package,
  Users,
  TrendingUp,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Store,
  Banknote,
  CreditCard
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { useAdminAuth } from '../../context/AdminAuthContext';

const AdminDashboard = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topBrands, setTopBrands] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [posSales, setPosSales] = useState({ summary: null, recent: [] });

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load stats
      const statsRes = await fetchWithAuth('/api/admin/dashboard/stats');
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      // Load top products
      const productsRes = await fetchWithAuth('/api/admin/dashboard/top-products?limit=5');
      if (productsRes.ok) {
        const data = await productsRes.json();
        setTopProducts(data.products);
      }

      // Load top brands
      const brandsRes = await fetchWithAuth('/api/admin/dashboard/top-brands?limit=5');
      if (brandsRes.ok) {
        const data = await brandsRes.json();
        setTopBrands(data.brands);
      }

      // Load POS sales summary
      const posSummaryRes = await fetchWithAuth('/api/admin/pos/sales/summary?period=today');
      if (posSummaryRes.ok) {
        const summaryData = await posSummaryRes.json();
        
        // Load recent POS sales
        const posRecentRes = await fetchWithAuth('/api/admin/pos/sales?limit=5');
        if (posRecentRes.ok) {
          const recentData = await posRecentRes.json();
          setPosSales({ summary: summaryData, recent: recentData.sales || [] });
        } else {
          setPosSales({ summary: summaryData, recent: [] });
        }
      }

    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  const loadChartData = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`/api/admin/dashboard/sales-chart?period=${chartPeriod}`);
      if (res.ok) {
        const data = await res.json();
        setChartData(data.data);
      }
    } catch (error) {
      console.error('Error loading chart:', error);
    }
  }, [fetchWithAuth, chartPeriod]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    loadChartData();
  }, [loadChartData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Sales Today */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <DollarSign className="text-green-500" size={24} />
            </div>
            <span className="text-xs text-gray-400 uppercase tracking-wider">Hoy</span>
          </div>
          <p className="text-2xl font-semibold text-white mb-1">
            {formatCurrency(stats?.sales?.today || 0)}
          </p>
          <p className="text-sm text-gray-400">
            {stats?.sales?.orders_today || 0} pedidos
          </p>
        </div>

        {/* Sales Month */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-amber-500/10 rounded-lg">
              <TrendingUp className="text-amber-500" size={24} />
            </div>
            <span className="text-xs text-gray-400 uppercase tracking-wider">Este mes</span>
          </div>
          <p className="text-2xl font-semibold text-white mb-1">
            {formatCurrency(stats?.sales?.month || 0)}
          </p>
          <p className="text-sm text-gray-400">
            {stats?.sales?.orders_month || 0} pedidos
          </p>
        </div>

        {/* Average Ticket */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <ShoppingCart className="text-blue-500" size={24} />
            </div>
            <span className="text-xs text-gray-400 uppercase tracking-wider">Ticket Prom.</span>
          </div>
          <p className="text-2xl font-semibold text-white mb-1">
            {formatCurrency(stats?.sales?.avg_ticket || 0)}
          </p>
          <p className="text-sm text-gray-400">Por pedido</p>
        </div>

        {/* Pending Orders */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <Package className="text-purple-500" size={24} />
            </div>
            <span className="text-xs text-gray-400 uppercase tracking-wider">Pendientes</span>
          </div>
          <p className="text-2xl font-semibold text-white mb-1">
            {stats?.counts?.pending_orders || 0}
          </p>
          <Link to="/admin/orders?status=pending" className="text-sm text-amber-500 hover:underline">
            Ver pedidos →
          </Link>
        </div>
      </div>

      {/* Charts & Top Products Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <div className="lg:col-span-2 bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-white">Ventas</h3>
            <div className="flex gap-2">
              {['7d', '30d', '90d', '12m'].map(period => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    chartPeriod === period 
                      ? 'bg-amber-500 text-black' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#9ca3af' }}
                  formatter={(value) => [formatCurrency(value), 'Ventas']}
                />
                <Area 
                  type="monotone" 
                  dataKey="sales" 
                  stroke="#f59e0b" 
                  fill="url(#salesGradient)" 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Más Vendidos</h3>
          
          {topProducts.length > 0 ? (
            <div className="space-y-4">
              {topProducts.map((product, index) => (
                <div key={product._id} className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center text-xs text-gray-400">
                    {index + 1}
                  </span>
                  <img 
                    src={product.product_image || 'https://via.placeholder.com/40'} 
                    alt={product.product_name}
                    className="w-10 h-10 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{product.product_name}</p>
                    <p className="text-xs text-gray-400">{product.total_sold} vendidos</p>
                  </div>
                  <p className="text-sm text-amber-500">{formatCurrency(product.total_revenue)}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Sin datos de ventas aún</p>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Brands */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Marcas Más Vendidas</h3>
          
          {topBrands.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topBrands} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                  <YAxis dataKey="_id" type="category" stroke="#9ca3af" fontSize={11} width={80} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    formatter={(value) => [value, 'Unidades']}
                  />
                  <Bar dataKey="total_sold" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Sin datos de ventas aún</p>
          )}
        </div>

        {/* Top Customer */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Cliente Más Frecuente</h3>
          
          {stats?.top_customer ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-amber-500" size={32} />
              </div>
              <p className="text-lg text-white font-medium">{stats.top_customer.name}</p>
              <p className="text-sm text-gray-400 mb-4">{stats.top_customer.email}</p>
              <div className="flex justify-center gap-6">
                <div>
                  <p className="text-2xl text-white font-semibold">{stats.top_customer.order_count}</p>
                  <p className="text-xs text-gray-400">Pedidos</p>
                </div>
                <div>
                  <p className="text-2xl text-amber-500 font-semibold">
                    {formatCurrency(stats.top_customer.total_spent)}
                  </p>
                  <p className="text-xs text-gray-400">Total gastado</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-8">Sin datos de clientes aún</p>
          )}
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Alertas de Stock</h3>
            <Link to="/admin/inventory" className="text-sm text-amber-500 hover:underline">
              Ver todo
            </Link>
          </div>
          
          {stats?.low_stock_alerts?.length > 0 ? (
            <div className="space-y-3">
              {stats.low_stock_alerts.map(product => (
                <div 
                  key={product.id} 
                  className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20"
                >
                  <AlertTriangle className="text-red-500 flex-shrink-0" size={18} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{product.product_code}</p>
                    <p className="text-xs text-gray-400">{product.name}</p>
                  </div>
                  <span className="text-sm font-medium text-red-400">
                    {product.stock} uds.
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package className="text-green-500" size={24} />
              </div>
              <p className="text-sm text-gray-400">Sin alertas de stock</p>
            </div>
          )}
        </div>
      </div>

      {/* POS Sales Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* POS Summary */}
        <div className="bg-gradient-to-br from-purple-900/50 to-gray-800 rounded-xl p-6 border border-purple-500/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <Store className="text-purple-400" size={20} />
              Ventas POS (Hoy)
            </h3>
            <Link to="/admin/pos" className="text-sm text-purple-400 hover:underline">
              Ir al POS →
            </Link>
          </div>
          
          {posSales.summary ? (
            <div className="space-y-4">
              <div className="text-center py-2">
                <p className="text-3xl font-bold text-white">{formatCurrency(posSales.summary.total_sales || 0)}</p>
                <p className="text-sm text-gray-400">{posSales.summary.total_transactions || 0} transacciones</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-500/10 rounded-lg p-3 text-center">
                  <Banknote className="mx-auto text-green-400 mb-1" size={20} />
                  <p className="text-lg font-semibold text-green-400">{formatCurrency(posSales.summary.cash_sales || 0)}</p>
                  <p className="text-xs text-gray-400">Efectivo</p>
                </div>
                <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                  <CreditCard className="mx-auto text-blue-400 mb-1" size={20} />
                  <p className="text-lg font-semibold text-blue-400">{formatCurrency(posSales.summary.card_sales || 0)}</p>
                  <p className="text-xs text-gray-400">Tarjeta</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Store className="mx-auto text-gray-600 mb-2" size={32} />
              <p className="text-sm text-gray-400">Sin ventas POS hoy</p>
            </div>
          )}
        </div>

        {/* Recent POS Sales */}
        <div className="lg:col-span-2 bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Ventas POS Recientes</h3>
          </div>
          
          {posSales.recent.length > 0 ? (
            <div className="space-y-3">
              {posSales.recent.map(sale => (
                <div 
                  key={sale.id} 
                  className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${sale.payment_method === 'cash' ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                      {sale.payment_method === 'cash' 
                        ? <Banknote className="text-green-400" size={18} />
                        : <CreditCard className="text-blue-400" size={18} />
                      }
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{sale.sale_number}</p>
                      <p className="text-xs text-gray-400">
                        {sale.location_name} • {sale.items?.length || 0} productos
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-white">{formatCurrency(sale.total)}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(sale.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ShoppingCart className="mx-auto text-gray-600 mb-2" size={32} />
              <p className="text-sm text-gray-400">No hay ventas POS recientes</p>
              <Link to="/admin/pos" className="text-sm text-purple-400 hover:underline mt-2 inline-block">
                Abrir terminal POS
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-lg p-4 text-center">
          <p className="text-3xl font-semibold text-white">{stats?.counts?.products || 0}</p>
          <p className="text-sm text-gray-400">Productos</p>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4 text-center">
          <p className="text-3xl font-semibold text-white">{stats?.counts?.users || 0}</p>
          <p className="text-sm text-gray-400">Clientes</p>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4 text-center">
          <p className="text-3xl font-semibold text-white">{stats?.counts?.orders || 0}</p>
          <p className="text-sm text-gray-400">Pedidos totales</p>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4 text-center">
          <p className="text-3xl font-semibold text-amber-500">{stats?.best_product?.product_name?.substring(0, 15) || '-'}...</p>
          <p className="text-sm text-gray-400">Producto estrella</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
